import { Component, Input, ViewEncapsulation } from '@angular/core';
import { Observable } from 'rxjs';
import _ from 'lodash';
import { SecurityType } from '@ipreo/muni-shared';
import { GameDayModel } from '../../../models/gameday-model';
import { formatRange } from '../../../tranche/pipes/format-range';
import { ACCOUNT } from '../../data/gameDay-data.calculation';
import { bondTypes, bondTypesLabels } from '../../mappers/gameDay.extractors';
import { AccountManager } from '../../../state/data/state';
import { amountFormat } from '../../gameday.constants';

export const PORTFOLIO_OVERVIEW_TOOLTIP_TEXT = 'Portfolio Overview';
export const NO_PORTFOLIO_OVERVIEW_DATA_TOOLTIP_TEXT =
  'No Portfolio Data Available';

@Component({
  selector: 'muni-gameday-card-header',
  templateUrl: './card-header.template.html',
  styleUrls: ['./card-header.styles.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CardHeaderComponent {
  public maturity: string;
  public securityTypeCode: string;
  public coupons: string;
  public series: string;

  @Input()
  public isLinkClickable: boolean;

  @Input()
  isOrderPeriodTerminated = false;

  public isOrderPeriodClosed = false;

  private _maturities: GameDayModel.Maturity[];
  private _entity: AccountManager;
  public amountFormatted: string;
  public orderCountFormatted: string;
  public showLink = false;
  public showClosedSymbol: Observable<boolean>;

  @Input()
  public set orderCount(orderCount: number) {
    this.orderCountFormatted =
      !orderCount && orderCount !== 0 ? '' : amountFormat.format(orderCount);
  }
  @Input()
  public set amount(amount: number) {
    this.amountFormatted =
      !amount && amount !== 0 ? '' : amountFormat.format(amount);
  }

  public get entity(): AccountManager {
    return this._entity;
  }

  @Input()
  public set entity(value: AccountManager) {
    this._entity = value;
    this.showLink = !!value && value.type === ACCOUNT;
  }

  @Input()
  public set maturities(
    maturities: GameDayModel.Maturity[] | null | undefined
  ) {
    // tslint:disable-line:no-null-keyword
    this._maturities = maturities;

    if (!maturities || maturities.length === 0) {
      this.reset();
      return;
    }
    this.setSeries(maturities);
    this.setCoupons(maturities);
    this.setMaturity(maturities);
    this.setSecurity(maturities);
  }

  public get maturities(): GameDayModel.Maturity[] {
    return this._maturities;
  }

  public preventClickEvent(event: MouseEvent) {
    if (!this.isLinkClickable) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  public getTitle(): string {
    return this.isLinkClickable
      ? PORTFOLIO_OVERVIEW_TOOLTIP_TEXT
      : NO_PORTFOLIO_OVERVIEW_DATA_TOOLTIP_TEXT;
  }

  private reset(): void {
    this.maturity = undefined;
    this.coupons = undefined;
    this.series = undefined;
  }

  private setMaturity(maturities: GameDayModel.Maturity[]) {
    this.maturity = maturities[0].maturityDate.format('MM/DD/YYYY');
  }

  private setSecurity(maturities: GameDayModel.Maturity[]) {
    const securityTypes: SecurityType[] = maturities.map(
      (maturity) => maturity.securityType
    );

    if (_.uniq(securityTypes).length > 1 ? true : false) {
      this.securityTypeCode = 'Multiple';
    } else {
      if (securityTypes[0] === SecurityType.bond) {
        this.securityTypeCode = maturities[0].series.isAMT ? 'AMT' : '';
      } else {
        this.securityTypeCode =
          bondTypesLabels[bondTypes.indexOf(securityTypes[0])];
      }
    }
  }

  private setCoupons(maturities: GameDayModel.Maturity[]): void {
    this.coupons = formatRange(
      maturities.map((m) =>
        m.coupon || m.coupon === 0 ? m.coupon : undefined
      ),
      'percent'
    );
  }

  private setSeries(maturities: GameDayModel.Maturity[]) {
    this.series = _(maturities)
      .map((m) => m.series.code)
      .uniq()
      .join(', ');
  }
}
